/**
 * member-edit.js — 會員「修改個人資料」表單互動
 *
 * 對照 HTML（src/pages/member/edit.html）：
 *   <form class="admin-member-form" data-member-edit novalidate>
 *     …基本資料 card（姓名 required / Email disabled / 手機 required）…
 *     …變更密碼 card：每欄 .member-pwd-field > input + button[data-pwd-toggle="<id>"]…
 *       新密碼 #member-pwd-new (minlength 8)、確認 #member-pwd-confirm
 *       <p class="form-field__error" id="member-pwd-confirm-error" role="alert" hidden></p>
 *     <div class="admin-form-actions">…<button type="submit" data-member-edit-submit>…
 *
 * 功能（MEMBER-SPEC §C2）：
 *   - 密碼欄顯示/隱藏 toggle：切 input type password↔text、eye↔eye-off icon、aria-pressed/label。
 *   - 新密碼即時檢核清單（.pw-checklist）：共用 password-checklist.js（三項硬性 ≥8 / 含英文 / 含數字 +
 *     確認一致），取代原靜態 hint 與孤兒 setNewHintState（form-field__hint--ok 無對應 CSS）。
 *   - 變更密碼為選填：新密碼欄非空時才要求清單三項 ✓ + 一致才解除送出；新密碼空（不改密碼）不擋。
 *   - 確認密碼不符仍顯既有 __error（role=alert，紅字），與清單一致指示 .pw-match 互斥（不同框）。
 *   - 送出：HTML5 checkValidity + 密碼一致性檢查 → 通過則 .is-submitting + inline spinner
 *     （reduced-motion 退「處理中…」純文字），模擬非同步成功後 window.AdminToast.show({type:'success'})。
 *     （本波無 backend，submit 不真正送出；接後端時把 simulate 換成 fetch 即可。）
 *
 * 載入：由 /src/admin.js 中央 import 鏈 auto-init（會員頁殼亦走 admin.js）；密碼清單共用 password-checklist.js。
 *   無對應 DOM（[data-member-edit] 不存在）即 no-op。
 *
 * 沿用既有慣例：form-field--invalid + [aria-invalid] 標紅（form-field.css）、AdminToast（admin-toast.js）。
 * TODO(backend): 本波無 backend，submit 為模擬成功（setTimeout + toast）；接後端時把 simulate 換成 fetch（見 initSubmit）。
 */

import { initPasswordChecklist } from './password-checklist.js';
import { prefersReducedMotion } from '../utils/dom.js';

const SPINNER_SVG =
  '<svg class="member-spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" ' +
  'stroke-dasharray="42" stroke-dashoffset="14" /></svg>';

/* ---------- 密碼顯示/隱藏 toggle ---------- */
function initPwdToggles(form) {
  form.querySelectorAll('[data-pwd-toggle]').forEach((btn) => {
    const input = document.getElementById(btn.getAttribute('data-pwd-toggle'));
    if (!input) return;
    const eye = btn.querySelector('.member-pwd-field__eye');
    const eyeOff = btn.querySelector('.member-pwd-field__eye-off');

    // SVG 元素是 SVGElement 而非 HTMLElement，不反射 `.hidden` IDL 屬性到 [hidden]，
    // 故用 toggleAttribute 直接切 [hidden]（CSS .member-pwd-field__toggle svg[hidden] 才吃得到）。
    const setHidden = (el, hide) => el && el.toggleAttribute('hidden', hide);
    btn.addEventListener('click', () => {
      const show = input.type === 'password';
      input.type = show ? 'text' : 'password';
      btn.setAttribute('aria-pressed', show ? 'true' : 'false');
      btn.setAttribute('aria-label', show ? '隱藏密碼' : '顯示密碼');
      setHidden(eye, show);
      setHidden(eyeOff, !show);
    });
  });
}

/* ---------- 欄位 invalid 標記輔助 ---------- */
function setInvalid(input, errorEl, message) {
  input.setAttribute('aria-invalid', 'true');
  const wrap = input.closest('.form-field');
  if (wrap) wrap.classList.add('form-field--invalid');
  if (errorEl && message != null) {
    errorEl.textContent = message;
    errorEl.hidden = false;
  }
}
function clearInvalid(input, errorEl) {
  input.removeAttribute('aria-invalid');
  const wrap = input.closest('.form-field');
  if (wrap) wrap.classList.remove('form-field--invalid');
  if (errorEl) {
    errorEl.textContent = '';
    errorEl.hidden = true;
  }
}

/* ---------- 即時驗證（新密碼 ≥8 / 確認相符） ----------
 * 新密碼的「逐項達成」由 password-checklist.js（.pw-checklist）即時呈現，本處只管：
 *   - validateNew：非空且 <8 → 標紅欄框（空值不報，交 submit/required）；達 8 清紅。
 *   - validateConfirm：與新密碼不符 → 標「確認新密碼」欄 __error（P1-7：不連帶標新密碼欄）；
 *     一致態的綠色指示走清單的 .pw-match（與此紅字互斥），故此處只負責「不符」紅字。 */
function initLiveValidation(form, refs) {
  const { newPwd, confirmPwd, confirmError } = refs;

  const validateNew = () => {
    if (newPwd.value && newPwd.value.length < 8) {
      setInvalid(newPwd, null);
      return false;
    }
    clearInvalid(newPwd, null);
    return true;
  };

  // 確認密碼：與新密碼不符 → 只標「確認新密碼」欄（P1-7：不連帶標新密碼欄）。
  // 關鍵（P1-7）：mismatch 屬「兩欄關聯」錯誤，但新密碼本身若合法（≥8）不應跟著亮紅；
  // 若新密碼本身不合法（<8），使用者該先修新密碼，不在確認欄再疊一個 mismatch 噪音 →
  // 此時不報 mismatch（交由 validateNew 標新密碼欄 + submit 擋）。
  // 文案統一為「兩次輸入的密碼不一致」（對齊 auth-forms.js，收斂兩處不一致用語）。
  const validateConfirm = () => {
    const newValid = newPwd.value.length >= 8;
    if (confirmPwd.value && newValid && confirmPwd.value !== newPwd.value) {
      setInvalid(confirmPwd, confirmError, '兩次輸入的密碼不一致');
      return false;
    }
    clearInvalid(confirmPwd, confirmError);
    return true;
  };

  newPwd.addEventListener('input', () => {
    validateNew();
    // 新密碼變動時，若確認欄已有值，連動重驗一致性
    if (confirmPwd.value) validateConfirm();
  });
  confirmPwd.addEventListener('input', validateConfirm);

  return { validateNew, validateConfirm };
}

/* ---------- submit：spinner + 模擬成功 toast ---------- */
// TODO(backend): 欄位改 /me 帶入，submit 把 simulate 換 fetch PUT 會員資料（驗舊密碼+寫新密碼） — 見 docs/BACKEND_HANDOFF.md#member-edit
function initSubmit(form, refs, validators, checklist, syncGate) {
  const submitBtn = form.querySelector('[data-member-edit-submit]');
  if (!submitBtn) return;
  const originalLabel = submitBtn.textContent;

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (form.classList.contains('is-submitting')) return;

    // 1) 清舊 invalid，跑 HTML5 內建驗證（required / minlength / type 等）
    let firstInvalid = null;
    const fields = form.querySelectorAll('input');
    fields.forEach((field) => {
      if (field.disabled || field.type === 'hidden') return;
      const wrap = field.closest('.form-field');
      field.removeAttribute('aria-invalid');
      if (wrap) wrap.classList.remove('form-field--invalid');
      if (!field.checkValidity()) {
        setInvalid(field, null);
        if (!firstInvalid) firstInvalid = field;
      }
    });

    // 2) 密碼一致性（覆蓋 HTML5 無法表達的「兩欄相符」）
    const newOk = validators.validateNew();
    const confirmOk = validators.validateConfirm();
    if (!newOk && !firstInvalid) firstInvalid = refs.newPwd;
    if (!confirmOk && !firstInvalid) firstInvalid = refs.confirmPwd;

    if (firstInvalid) {
      firstInvalid.focus();
      return;
    }

    // 3) 通過 → spinner（reduced-motion 純文字「處理中…」），模擬非同步成功
    form.classList.add('is-submitting');
    submitBtn.setAttribute('aria-disabled', 'true');
    submitBtn.disabled = true;
    submitBtn.innerHTML = prefersReducedMotion()
      ? '處理中…'
      : SPINNER_SVG + '<span>儲存中…</span>';

    window.setTimeout(() => {
      form.classList.remove('is-submitting');
      submitBtn.removeAttribute('aria-disabled');
      submitBtn.disabled = false;
      submitBtn.textContent = originalLabel;

      if (window.AdminToast && typeof window.AdminToast.show === 'function') {
        window.AdminToast.show({
          type: 'success',
          title: '已儲存',
          text: '您的個人資料已更新。',
        });
      }
      // 清空密碼欄（成功後不留明碼）
      [refs.oldPwd, refs.newPwd, refs.confirmPwd].forEach((el) => {
        if (el) el.value = '';
      });
      // 密碼清空後重置清單狀態（項回 pending / .pw-match 收起 / aria-live 歸零）並重算 gate。
      if (checklist) checklist.refresh();
      if (typeof syncGate === 'function') syncGate();
    }, 900);
  });
}

function init(root = document) {
  const form = root.querySelector('[data-member-edit]');
  if (!form) return; // no-op：非 edit 頁

  const refs = {
    oldPwd: form.querySelector('#member-pwd-old'),
    newPwd: form.querySelector('#member-pwd-new'),
    confirmPwd: form.querySelector('#member-pwd-confirm'),
    confirmError: form.querySelector('#member-pwd-confirm-error'),
  };

  initPwdToggles(form);
  if (refs.newPwd && refs.confirmPwd) {
    const validators = initLiveValidation(form, refs);

    // 變更密碼為選填區的 gate：新密碼非空時才要求清單三項 ✓ + 一致；新密碼空（不改密碼）不擋。
    // 先宣告 syncGate 再 init 清單（避免 init 同步評估時 TDZ）。
    const submitBtn = form.querySelector('[data-member-edit-submit]');
    let checklist = null;
    const syncGate = () => {
      if (!submitBtn) return;
      // 新密碼空 = 不改密碼 → 不擋；非空 → 須 checklist.isValid()。
      const ok = refs.newPwd.value === '' || (checklist ? checklist.isValid() : true);
      submitBtn.disabled = !ok;
      submitBtn.setAttribute('aria-disabled', ok ? 'false' : 'true');
    };
    checklist = initPasswordChecklist({
      pwd: refs.newPwd,
      confirm: refs.confirmPwd,
      confirmError: refs.confirmError,
      onChange: syncGate,
    });
    syncGate(); // 初始：新密碼空 → 不擋（可送出僅改基本資料）

    initSubmit(form, refs, validators, checklist, syncGate);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}
